<script lang="ts" setup>
import SkrButton from '@/components/button/SkrButton.vue'
import DirectSignArrow from '@/components/direct-sign/DirectSignArrow.vue'
import DirectSignId from '@/components/direct-sign/DirectSignId.vue'
import DirectSignScan from '@/components/direct-sign/DirectSignScan.vue'
import DirectSignViewer from '@/components/direct-sign/DirectSignViewer.vue'
import SkrHeading from '@/components/SkrHeading.vue'
import OverlayBase from '@/components/overlays/OverlayBase.vue'
import OverlayHeaderLogo from '@/components/overlays/OverlayHeaderLogo.vue'
import QrCode from '@/components/QrCode.vue'
import IdentFaqList from '@/components/identification/IdentFaqList.vue'
import useIdentificationTheme from '@/composables/useIdentificationTheme.ts'

const props = defineProps<{
  signatureRequest: SignatureRequestData | null
  isDesktop: boolean
}>()
const widgetStore = useWidgetStore()
const scrollSpacer = ref<HTMLElement | null>(null)
const viewer = ref<boolean>(false)
const { t } = useI18n()
const route = useRoute()
const languageStore = useLanguageStore()

const url = computed(() => {
  const currentUrl = new URL(route.fullPath, window.location.origin)
  currentUrl.searchParams.set('showOverlay', 'false')
  const lang = languageStore.language
  if (lang) {
    currentUrl.searchParams.set('lang', lang)
  }
  return currentUrl.toString()
})

const hideOverlay = () => {
  widgetStore.activeOverlay = null
}

const invitingBusinessId = computed(() => props.signatureRequest?.businessId ?? '')

const { theme: identificationTheme, publicBusinessInfo, rootStyle } = useIdentificationTheme(invitingBusinessId)

const miniLogoUrl = computed(() => publicBusinessInfo.value?.companyBranding?.miniLogo?.url ?? '')
const brandingLogoUrl = computed(() => publicBusinessInfo.value?.companyBranding?.logo?.url ?? '')

const helpCenterUrl = computed(
  () => identificationTheme.value?.branding.helpCenterUrl || t('mab_direct_sign.q_and_a.footer_help_url')
)

const activeFAQPanel = ref<'documentsUsed' | null>(null)

const openSecondFAQPanel = () => {
  activeFAQPanel.value = 'documentsUsed' // open the documentsUsed panel
}
</script>

<template>
  <overlay-base class="overlay-direct-sign" data-cy="overlay_direct_sign" scroll-strategy="none">
    <template #header>
      <overlay-header-logo floating :company-branding-logo-url="brandingLogoUrl" />
    </template>
    <div class="identification-typography" :style="rootStyle">
      <direct-sign-viewer v-if="isDesktop" v-model:visible="viewer" :signature-request="signatureRequest" />
      <v-container class="overlay__body" :class="{ 'fill-height': isDesktop }">
        <div
          v-if="isDesktop"
          class="w-100 h-auto d-flex flex-column justify-center align-center overlay-direct-sign__top"
        >
          <div class="overlay-direct-sign__top-spacer">
            <div class="rounded-xl bg-white w-100 | overlay-direct-sign__info-block">
              <v-row>
                <v-col cols="12" md="7">
                  <div class="pa-8">
                    <skr-heading level="1" semantic>{{ t('mab_direct_sign.qr_code_section.title') }} </skr-heading>
                    <ol class="pt-6 overlay-direct-sign__steps">
                      <li>{{ t('mab_direct_sign.qr_code_section.step_1') }}</li>
                      <li>{{ t('mab_direct_sign.qr_code_section.step_2') }}</li>
                    </ol>
                    <div class="mt-12">
                      <v-btn
                        color="tertiary-xqes"
                        size="large"
                        variant="outlined"
                        border="thin"
                        height="62"
                        class="tertiary-button"
                        data-cy="overlay_direct_sign_viewer_button"
                        @click="viewer = true"
                      >
                        <v-icon class="mr-3">custom:eye_open</v-icon>
                        {{ t('mab_direct_sign.qr_code_section.view_document') }}
                      </v-btn>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" md="5">
                  <div class="pa-12 h-100 d-flex justify-center align-center">
                    <qr-code :value="url" :image="miniLogoUrl" data-cy="overlay_direct_sign_qr_code" />
                  </div>
                </v-col>
              </v-row>
            </div>
          </div>
          <div ref="scrollSpacer" class="overlay-direct-sign__top-spacer">
            <v-btn
              color="grey-light-2 btn-text rounded-pill"
              class="mx-0 text-grey-dark-2"
              size="large"
              height="64"
              flat
              @click="scrollSpacer?.scrollIntoView({ behavior: 'smooth' })"
            >
              {{ t('mab_direct_sign.how_it_works.title') }}
            </v-btn>
          </div>
        </div>
        <div class="w-100 d-flex flex-column justify-center align-center pt-16">
          <v-row class="position-relative">
            <div v-if="isDesktop" class="overlay-direct-sign__panel__arrow">
              <direct-sign-arrow />
            </div>
            <v-col v-if="isDesktop" cols="12" md="6" class="d-flex justify-center">
              <div class="bg-grey-light-2 rounded-xl | overlay-direct-sign__panel">
                <div class="d-flex justify-center mb-13">
                  <direct-sign-scan />
                </div>
                <skr-heading level="1" semantic> {{ t('mab_direct_sign.how_it_works.step_1.title') }} </skr-heading>
                <p class="text-grey-dark-2 pt-7">
                  {{ t('mab_direct_sign.how_it_works.step_1.description') }}
                </p>
              </div>
            </v-col>
            <v-col cols="12" md="6" class="d-flex justify-center">
              <div :class="{ 'pt-16': isDesktop }">
                <div class="rounded-xl | overlay-direct-sign__panel" :class="{ 'bg-grey-light-2': isDesktop }">
                  <div class="d-flex justify-center mb-13">
                    <direct-sign-id />
                  </div>
                  <skr-heading level="1" semantic> {{ t('mab_direct_sign.how_it_works.step_2.title') }} </skr-heading>
                  <p class="text-grey-dark-2 pt-7">
                    <i18n-t keypath="mab_direct_sign.how_it_works.step_2.description">
                      <template #link>
                        <nuxt-link class="cursor-pointer" @click.prevent="openSecondFAQPanel">{{
                          t('mab_direct_sign.how_it_works.step_2.link')
                        }}</nuxt-link>
                      </template>
                    </i18n-t>
                  </p>
                </div>
              </div>
            </v-col>
          </v-row>
          <v-row class="overlay-direct-sign__faq">
            <div class="w-100">
              <div class="d-flex justify-center align-center mb-10">
                <skr-divider />
                <span class="max-content px-4 font-weight-bold text-on-surface-xqes">{{
                  t('mab_direct_sign.q_and_a.title')
                }}</span>
                <skr-divider />
              </div>
              <ident-faq-list v-model:active-panel="activeFAQPanel" :scroll-to="['documentsUsed']" />
              <p class="text-center mt-12 faq-text">
                <i18n-t
                  keypath="mab_direct_sign.q_and_a.footer"
                  tag="span"
                  for="mab_direct_sign.q_and_a.footer_help_label"
                >
                  <a :href="helpCenterUrl" target="_blank" rel="noopener">{{
                    t('mab_direct_sign.q_and_a.footer_help_label')
                  }}</a>
                </i18n-t>
              </p>
            </div>
          </v-row>
        </div>
      </v-container>
      <div
        v-if="!isDesktop"
        class="position-sticky bottom-0 left-0 right-0 bg-white d-flex justify-center pa-3 get-started"
      >
        <skr-button
          color="primary-xqes"
          size="lg"
          width="100%"
          height="52"
          data-cy="overlay_direct_sign_get_started"
          @click="hideOverlay"
          >{{ t('mab_direct_sign.get_started') }}</skr-button
        >
      </div>
    </div>
  </overlay-base>
</template>

<style lang="scss" scoped>
.get-started {
  z-index: 9999;
  border-top: 1px solid $c-border;
  button {
    max-width: 500px;
  }
}
.max-content {
  min-width: max-content;
}
.faq-text {
  color: $c-grey-medium-2;
}
h1 {
  font-size: 2rem !important;
}
:deep(.v-toolbar__content),
.sh {
  position: sticky !important;
  top: 0;
  left: 0;
  z-index: 99999;
  background-color: $c-white;
}
:deep(.v-toolbar__content) {
  @include miw(sm) {
    border-bottom: 1px solid $c-border;
  }
}
.btn-text {
  @include miw(md) {
    font-size: 1.25rem;
  }
}
.tertiary-button {
  :deep(.v-btn__overlay) {
    display: none;
  }
  border-color: color-mix(in srgb, rgb(var(--v-theme-tertiary-xqes)) 20%, transparent) !important;
  transition: border-color 0.2s;
  &:hover {
    border-color: rgb(var(--v-theme-tertiary-xqes)) !important;
  }
}
.overlay-direct-sign {
  &__body {
    z-index: 1;
  }
  &__info-block {
    max-width: 868px;
    box-shadow: 0px 20px 35px 3px rgba(40, 62, 102, 0.14);
    button {
      font-size: 1rem;
    }
  }
  &__top {
    min-height: 100%;
  }
  &__top-spacer {
    padding-top: calc(#{$bw} * 36);
  }
  &__faq {
    width: 100%;
    max-width: 665px;
    padding-top: calc(#{$bw} * 28);
    padding-bottom: calc(#{$bw} * 55);
  }
  &__panel {
    max-width: 440px;
    min-height: 500px;
    padding: calc(#{$bw} * 4.5 * 4);
    @include maw(sm) {
      padding-top: calc(#{$bw} * 2 * 4);
    }
    &__arrow {
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      top: calc(4.5rem + 200px + 2rem);
      @include maw(sm) {
        display: none;
      }
    }
  }
  &__steps {
    list-style: decimal inside;
    margin: 0;
    li {
      position: relative;
      margin-bottom: 1.25rem;
      font-size: 1rem;
      line-height: 1.5;
      color: $c-text-alt;
      padding-left: calc(#{$bw} * 10);
      &::marker {
        content: '';
      }
      &::before {
        content: counter(list-item);
        position: absolute;
        left: 0;
        top: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        border: 1.5px solid rgb(var(--v-theme-on-surface-high-xqes));
        border-radius: 50%;
        color: rgb(var(--v-theme-on-surface-high-xqes));
        font-size: 14px;
        background: $c-white;
        line-height: 1;
      }
    }
  }
}
</style>
